import type { Customer, CustomerLocation } from '@/services/customer.service';
import type { AddressSelection } from '@/services/equipment-intake.service';
import type { AddressSide } from './address-side';

/**
 * Client-side PREVIEW of what the server will freeze.
 *
 * ARCHITECTURE — this module renders, it does not decide:
 *
 *  1. The preview is display-only. The form still submits a SELECTION
 *     ({ source, locationId }); the server re-resolves it inside the intake
 *     transaction and writes the snapshot. Nothing computed here is ever posted,
 *     so a tampered client still cannot put a GSTIN on an invoice.
 *
 *  2. Because the server is the authority, this mapping must mirror
 *     `resolveBillTo` / `resolveShipTo` in the backend intake service exactly.
 *     If the two drift, the operator is shown one address and a different one is
 *     frozen — worse than showing nothing. `address-preview.test.ts` pins the
 *     mapping; update both sides together or not at all.
 *
 *  3. Ship-To has no `gstNumber` member at all — not a null, not a hidden field.
 *     A shipping destination has no tax identity, and a value that exists but
 *     isn't rendered is one refactor away from being rendered.
 */

interface BaseAddressPreview {
  label: string;
  /** null when master data has no usable address line — the server REJECTS this. */
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  phone: string | null;
}

/** Carries the customer's GST — even when the address itself is a branch's. */
export interface BillToPreview extends BaseAddressPreview {
  gstNumber: string | null;
}

/** No gstNumber member. See rule 3 above. */
export type ShipToPreview = BaseAddressPreview;

export type AddressPreview = BillToPreview | ShipToPreview;

/** Mirrors the server's `requireAddressLine`: trimmed-empty is not an address. */
function addressLine(primary: string | null, fallback: string | null): string | null {
  const trimmed = (primary ?? fallback ?? '').trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Resolves a selection against already-fetched master data.
 *
 * Returns null when there is nothing to preview yet (no customer chosen, or a
 * branch selected whose record hasn't loaded). Callers render nothing in that
 * case rather than an empty card.
 */
export function resolveAddressPreview(
  side: AddressSide,
  selection: AddressSelection,
  customer: Customer | null,
  locations: CustomerLocation[],
): AddressPreview | null {
  if (!customer) return null;

  // GST always comes from the CUSTOMER, never from a branch (locked rule), and
  // only ever for Bill-To.
  const gst = side === 'Bill-To' ? { gstNumber: customer.gstNumber ?? null } : {};

  if (selection.source === 'CUSTOMER_DEFAULT') {
    return {
      label: customer.name,
      line1: addressLine(customer.addressLine1, customer.address),
      line2: customer.addressLine2 ?? null,
      city: customer.city ?? null,
      state: customer.state ?? null,
      postalCode: customer.postalCode ?? null,
      country: customer.country ?? null,
      phone: customer.phone ?? null,
      ...gst,
    };
  }

  const location = locations.find((l) => l.id === selection.locationId);
  if (!location) return null;

  return {
    label: location.name,
    line1: addressLine(location.addressLine1, location.address),
    line2: location.addressLine2 ?? null,
    city: location.city ?? null,
    state: location.state ?? null,
    postalCode: location.postalCode ?? null,
    country: location.country ?? null,
    phone: location.phone ?? null,
    ...gst,
  };
}
